import type { ExamQuestionReportItem } from '../../../api/exam'
import type { GroupItem } from '../../../lib/questionGroups'
import { PassagePanel } from './PassagePanel'
import { ListeningPlayer } from './ListeningPlayer'
import { ClozeRenderer } from './ClozeRenderer'
import { ClozeAnswerCard } from './ClozeAnswerCard'
import { LatexText } from '../../../components/ui/LatexText'
import { getFileUrl } from '../../../api/qb'

interface CompositeQuestionProps {
  group: GroupItem
  activeSubIndex: number
  onSubIndexChange: (index: number) => void
  answerMap: Record<string, { value: string; images: string[]; strokes?: string }>
  onAnswerChange: (questionId: string, value: string) => void
  onSelectChoice: (q: ExamQuestionReportItem, label: string) => void
  onGrade: (questionId: string) => void
  onExplain: (questionId: string) => void
  gradingMap: Record<string, boolean>
  isReadOnly: boolean
  // 测试模式：作答中隐藏所有智能讲解/批改入口，提交后统一批改
  testMode?: boolean
}

/**
 * Renders a composite question group.
 * - Cloze/grammar_fill: full-width passage with inline choice blanks + answer card
 * - Others (reading_comp, listening, etc.): passage on top, all sub-questions below
 */
export function CompositeQuestion({
  group,
  activeSubIndex,
  onSubIndexChange,
  answerMap,
  onAnswerChange,
  onSelectChoice,
  onGrade,
  onExplain,
  gradingMap,
  isReadOnly,
  testMode,
}: CompositeQuestionProps) {
  const subQ = group.subQuestions[activeSubIndex]
  if (!subQ) return null

  const isCloze = group.groupType === 'cloze' || group.groupType === 'grammar_fill' || group.groupType === 'seven_choose_five'
  const isListening = group.groupType === 'listening'

  // ─── Cloze: full-width layout ───────────────────────────────
  if (isCloze && group.passageContent) {
    // Extract blank numbers from passage (e.g. __17__, __18__) to map sub-questions correctly
    const passageBlankNums = [...group.passageContent.matchAll(/__(\d+)__/g)]
      .map(m => parseInt(m[1], 10))
      .sort((a, b) => a - b)

    const clozeBlanks = group.subQuestions.map((sq, i) => {
      const sqContent = sq.content || {}
      // Extract blank number from stem (e.g. "__11__" → 11), fallback to sequential
      const stemStr = (sqContent.stem as string) || ''
      const stemMatch = stemStr.match(/^__(\d+)__$/)
      let blankIndex = stemMatch ? parseInt(stemMatch[1], 10) : i + 1

      // If stem-based index doesn't exist in passage but passage has blanks,
      // map by position (handles stem __1__ vs passage __17__ mismatch)
      if (passageBlankNums.length > 0 && !passageBlankNums.includes(blankIndex)) {
        blankIndex = passageBlankNums[i] ?? blankIndex
      }

      // For seven_choose_five, use group-level options; otherwise use per-question options
      const blankOptions = group.groupType === 'seven_choose_five'
        ? group.groupOptions
        : (sq.options as { label: string; content: string }[] | null) || null

      return {
        index: blankIndex,
        questionId: sq.question_id,
        value: answerMap[sq.question_id]?.value || '',
        options: blankOptions,
        onChange: (v: string) => onAnswerChange(sq.question_id, v),
        readOnly: isReadOnly,
        isCorrect: sq.is_correct ?? null,
        correctAnswer: sq.correct_answer ?? null,
      }
    })

    const onGradeAll = async () => {
      for (const sq of group.subQuestions) {
        if (answerMap[sq.question_id]?.value && !sq.is_marked) {
          await onGrade(sq.question_id)
        }
      }
    }

    return (
      <div className="space-y-4">
        {/* Passage with inline blanks — full width */}
        <div className="rounded-lg border border-slate-200 bg-white p-5">
          <ClozeRenderer
            passage={group.passageContent}
            blanks={clozeBlanks}
          />
        </div>

        {/* Answer card */}
        <ClozeAnswerCard
          blanks={clozeBlanks}
          activeBlank={null}
          onBlankClick={() => {}}
          onGradeAll={testMode ? undefined : onGradeAll}
          isGrading={Object.values(gradingMap).some(Boolean)}
          readOnly={isReadOnly}
        />
      </div>
    )
  }

  // ─── Non-cloze: top-bottom layout (reading_comp, listening, etc.) ───
  return (
    <div className="space-y-4">
      {/* Passage / audio */}
      {isListening && group.audioUrl && (
        <ListeningPlayer audioUrl={group.audioUrl} />
      )}

      {group.passageContent && (
        <PassagePanel
          content={group.passageContent}
          images={group.groupImages}
        />
      )}

      {/* Group-level options (e.g., seven_choose_five candidates) */}
      {group.groupOptions && group.groupOptions.length > 0 && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
          <div className="text-xs font-semibold text-amber-700 mb-2">选项池</div>
          <div className="space-y-1">
            {group.groupOptions.map((opt, idx) => (
              <div key={opt.label || idx} className="text-sm text-slate-700">
                <span className="font-medium text-amber-800">{opt.label}.</span>{' '}
                <LatexText>{opt.content}</LatexText>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* All sub-questions */}
      {group.subQuestions.map((sq, sqIdx) => {
        const sqContent = sq.content || {}
        let sqStem = (sqContent.stem as string) || ''
        // Clean up trailing question-UUID links that appear as plain text in stem
        sqStem = sqStem.replace(/\s*question-[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}(-\d+)?\s*$/gi, '')
        const sqOptions = sq.options || []
        // Filter out invalid or duplicate image links
        const sqImages = ((sqContent.images as string[]) || []).filter(url => {
          if (!url) return false
          // Filter out question-UUID format links
          if (/^question-[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}(-\d+)?$/i.test(url)) return false
          // Skip images already embedded in stem via Markdown ![](url)
          if (sqStem.includes(url)) return false
          return true
        })
        const sqType = sq.type || 'choice'
        const sqAnswer = answerMap[sq.question_id] || { value: '', images: [] }
        const sqGrading = gradingMap[sq.question_id] || false

        return (
          <div key={sq.question_id} className="rounded-lg border border-slate-200 bg-white p-4 space-y-3">
            {/* Stem */}
            {sqType !== 'fill_blank' && (
              <div className="text-slate-900">
                <span className="text-sm font-semibold text-slate-500 mr-2">{sqIdx + 1}.</span>
                <LatexText className="text-base font-medium" inline>{sqStem}</LatexText>
              </div>
            )}

            {/* Sub-question images */}
            {sqImages.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {sqImages.map((img, idx) => (
                  <img
                    key={idx}
                    src={getFileUrl(img)}
                    alt={`sub-q-img-${idx}`}
                    className="max-h-48 rounded border border-slate-200 object-contain"
                  />
                ))}
              </div>
            )}

            {/* Matching: dropdown select using shared group options */}
            {group.groupType === 'matching' && group.groupOptions && group.groupOptions.length > 0 && (() => {
              const isCorrect = sq.is_correct === true
              const isWrong = sq.is_correct === false
              const hasGraded = sq.is_marked === true
              return (
                <div className="flex items-center gap-3">
                  <select
                    value={sqAnswer.value || ''}
                    onChange={(e) => onSelectChoice(sq, e.target.value)}
                    disabled={isReadOnly}
                    className={[
                      'rounded-lg border px-3 py-2 text-sm transition-colors appearance-none bg-white pr-8',
                      hasGraded && isCorrect ? 'border-emerald-500 bg-emerald-50 text-emerald-800' : '',
                      hasGraded && isWrong ? 'border-red-500 bg-red-50 text-red-800' : '',
                      !hasGraded && sqAnswer.value ? 'border-blue-500 bg-blue-50' : '',
                      !hasGraded && !sqAnswer.value ? 'border-slate-300 text-slate-400' : '',
                      isReadOnly ? 'cursor-not-allowed opacity-70' : 'cursor-pointer',
                    ].filter(Boolean).join(' ')}
                  >
                    <option value="" disabled>请选择</option>
                    {group.groupOptions!.map((opt) => (
                      <option key={opt.label} value={opt.label}>{opt.label}. {opt.content.length > 60 ? opt.content.slice(0, 60) + '...' : opt.content}</option>
                    ))}
                  </select>
                  {hasGraded && isCorrect && (
                    <span className="text-sm font-medium text-emerald-600">✓ 正确</span>
                  )}
                  {hasGraded && isWrong && (
                    <span className="text-sm font-medium text-red-600">
                      ✗ 正确答案: {sq.correct_answer}
                    </span>
                  )}
                </div>
              )
            })()}

            {/* Options: 2-column grid */}
            {sqType === 'choice' && group.groupType !== 'matching' && sqOptions.length > 0 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {sqOptions.map((opt, idx) => {
                  const label = opt.label || String.fromCharCode(65 + idx)
                  const text = (opt.content as string) || ''
                  const isSelected = sqAnswer.value === label
                  return (
                    <button
                      key={label}
                      type="button"
                      onClick={() => onSelectChoice(sq, label)}
                      disabled={isReadOnly}
                      className={[
                        'flex items-start rounded-lg border px-3 py-2 text-left text-sm transition-colors',
                        'border-slate-200 bg-white hover:bg-slate-50',
                        isSelected ? 'border-emerald-600 bg-emerald-100 text-emerald-950 shadow-sm ring-2 ring-emerald-200' : '',
                        isReadOnly ? 'cursor-not-allowed opacity-70' : '',
                      ].filter(Boolean).join(' ')}
                    >
                      <span
                        className={[
                          'mr-2 mt-0.5 inline-flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full text-xs font-semibold',
                          isSelected ? 'bg-emerald-600 text-white' : 'bg-slate-100 text-slate-600',
                        ].join(' ')}
                      >
                        {label}
                      </span>
                      <span className="text-sm text-slate-800">
                        <LatexText>{text}</LatexText>
                      </span>
                    </button>
                  )
                })}
              </div>
            )}

            {/* Essay input */}
            {(sqType === 'essay' || sqType === '解答题') && (
              <textarea
                className="w-full min-h-[100px] rounded-md border-2 border-blue-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="在这里作答..."
                value={sqAnswer.value}
                onChange={(e) => onAnswerChange(sq.question_id, e.target.value)}
                disabled={isReadOnly}
              />
            )}

            {/* Action buttons + grading result */}
            <div className="flex items-center justify-between">
              {!testMode && (
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => onExplain(sq.question_id)}
                  className="inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-md text-purple-700 bg-purple-50 hover:bg-purple-100"
                >
                  智能讲解
                </button>
                <button
                  type="button"
                  onClick={() => onGrade(sq.question_id)}
                  disabled={sqGrading || isReadOnly}
                  className={`inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-md text-white ${
                    sqGrading || isReadOnly ? 'bg-indigo-400' : 'bg-indigo-600 hover:bg-indigo-700'
                  } disabled:opacity-50`}
                >
                  {sqGrading ? '批改中...' : 'AI批改'}
                </button>
              </div>
              )}

              {/* Inline grading badge */}
              {sq.is_marked && (
                <span className={`px-2 py-0.5 rounded text-xs font-bold ${sq.is_correct ? 'bg-green-200 text-green-800' : 'bg-red-200 text-red-800'}`}>
                  {sq.is_correct ? '✓ 正确' : '✗ 错误'}
                  {sq.score_obtained !== undefined && ` · ${sq.score_obtained}分`}
                </span>
              )}
            </div>

            {/* Grading feedback (expanded) */}
            {(sq.feedback || (sq.analysis && sq.is_marked)) && (
              <div className="p-3 border rounded-md bg-green-50 border-green-200 text-sm">
                {sq.correct_answer && (
                  <div className="text-xs text-slate-600 mb-1">答案: <LatexText>{sq.correct_answer}</LatexText></div>
                )}
                {sq.feedback && (
                  <div className="text-xs text-slate-700">{sq.feedback}</div>
                )}
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}
